import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { VsArticleTable } from "@/components/VsArticleTable";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/custom-web-design-vs-diy-platforms";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/custom-web-design-vs-diy-platforms")({
  head: () => ({
    meta: [
        { name: "description", content: "Custom Colorado web design vs. Wix, Squarespace, and GoDaddy. Ownership, SEO impact, and the real 5-year cost of each path for serious businesses." },
      { title: "Custom Web Design vs. DIY Platforms: 5-Year Cost" },
      { name: "description", content: ARTICLE?.desc ?? "" },
      { property: "og:title", content: "Custom Web Design vs. DIY Platforms: 5-Year Cost" },
      { property: "og:description", content: ARTICLE?.desc ?? "" },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({ title: ARTICLE?.title ?? "", description: ARTICLE?.desc ?? "" }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Buyer's Guide"
      read="8 min read"
      title="Custom web design vs. national DIY platforms"
      intro="Custom web design wins on ownership, SEO foundation, and 5-year cost. DIY platforms are cheaper and faster to launch. Which one matters for your business — and your Colorado Springs SEO footprint — depends on what the site is actually for."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "Custom design vs. DIY platforms" },
      ]}
      path={PATH}
      faqs={[
        { q: "Do I really not own the site on a DIY platform?", a: "You own your content. You don't own the templates, the code, the hosting, or the database structure. Try to export a DIY site and rebuild it elsewhere — you'll see what 'lock-in' actually means." },
        { q: "Can a DIY platform site rank well in Google?", a: "It can rank — for low-competition keywords. For competitive local searches (HVAC, plumbing, roofing in a major Colorado metro), the schema, page speed, and content depth limitations of most DIY platforms put you at a structural disadvantage versus competitors on custom builds." },
        { q: "What's the 5-year total cost difference?", a: "A custom site at $3,500 one-time + $50/month hosting comes to roughly $6,500 over 5 years. A DIY platform at $30–$50/month plus add-ons and theme upgrades typically runs $4,000–$8,000 over 5 years — and you don't own what you paid for." },
        { q: "When is a DIY platform the right call?", a: "When you need a site live in 48 hours, your budget is under $1,000 total, and you don't expect organic search to be a meaningful lead source." },
      ]}
    >
      <p data-speakable>
        Custom web design gives you a site you own outright — code, content, hosting, and search foundation. A national DIY platform is faster and cheaper to start, but you're renting templates and locked to their hosting. For businesses that depend on organic search — including <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">the Front Range clients</Link> we build for — custom web design is the only path that compounds. Choose based on whether your website is a brochure or your primary lead source.
      </p>

      <h2>Structural differences</h2>
      <VsArticleTable
        caption="Custom web design vs. national DIY platform"
        headerA="Custom-built site"
        headerB="National DIY platform"
        rows={[
          { label: "You own the site and the code", a: true, b: false },
          { label: "Custom design (not a template)", a: true, b: "Template library" },
          { label: "Built around local buyer behavior", a: true, b: "Generic" },
          { label: "Local SEO foundation", a: "Built in", b: "Add-on / limited" },
          { label: "Page speed (Lighthouse 90+)", a: true, b: "Often 50–70" },
          { label: "Custom schema markup", a: true, b: "Limited" },
          { label: "Cost to move off the platform later", a: "$0 — it's yours", b: "Full rebuild" },
          { label: "Recurring subscription required", a: false, b: true },
        ]}
      />

      <h2>Outcome differences — what users and Google actually see</h2>
      <VsArticleTable
        caption="What the experience and ranking outcomes look like"
        headerA="Custom-built site"
        headerB="National DIY platform"
        rows={[
          { label: "Time to launch", a: "4–8 weeks", b: "Same day–2 weeks" },
          { label: "Conversion rate optimization", a: "Owner-led, ongoing", b: "DIY / paid add-on" },
          { label: "Indexable by AI search engines", a: "Optimized", b: "Limited" },
          { label: "Custom integrations (CRM, scheduling)", a: "Anything", b: "Marketplace only" },
          { label: "5-year total cost (Colorado small biz)", a: "~$6,500", b: "~$4,000–$8,000" },
        ]}
      />

      <h2>When a DIY platform is the right call</h2>
      <ul>
        <li>You need a site live this week and the content matters more than the format.</li>
        <li>Your business doesn't depend on organic search — you have a strong referral or social channel already.</li>
        <li>Your total website budget is under $1,000 and you're willing to manage updates yourself.</li>
        <li>You're testing a brand-new business and don't yet know if it'll have legs.</li>
      </ul>

      <h2>The honest math at year three on custom web design</h2>
      <p>
        For a Colorado service business that depends on organic search, the gap usually widens by year two. Custom web design with proper local SEO foundations compounds traffic. DIY sites typically plateau and start losing ground to competitors who invested in custom web design — at which point the rebuild is more expensive than building it right the first time.
      </p>

      <p>
        Pair your build with <Link to="/colorado-springs-seo" className="font-bold text-brand-gold-deep underline">Colorado Springs SEO</Link>, see related: <Link to="/learn/web-design-vs-freelancer" className="font-bold text-brand-gold-deep underline">agency vs. freelancer for the build itself</Link>, or our <Link to="/pricing" className="font-bold text-brand-gold-deep underline">published web design packages</Link>.
      </p>

      <p className="text-sm text-foreground/60 italic">
        This guide compares category labels — not specific competitors. See <Link to="/editorial-policy" className="underline">how we source and review claims</Link>.
      </p>
    </ArticleLayout>
  );
}
